import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { styled } from 'styled-components';

import { Case } from '../../../../shared/api/hscan/case';
import { useUserState } from '../../../../shared/hooks/accountHook';
import { useCase } from '../../../../shared/hooks/caseHook';
import refreshIcon from '../../assets/icons/icon_refresh.svg';
import { ButtonWithIcon } from '../../common/components/ButtonWithIcon';
import { FilterSearchBar } from '../../common/components/FilterSearchBar';
import { CaseFilter } from '../../common/components/FilterSearchBar/SearchModal';
import { useServerSideSelected } from '../../common/components/SortableTable';
import {
  CurrentTimeText,
  ImageListTop,
  SectionContentContainer,
  SectionHeading,
  Tab,
  Tabs,
} from '../../common/components/styledComponents';

import { ImageListTable } from './ImageListTable';
import { SendToInstitutionTab } from './SendToInstitutionTab';
import { ShareToDoctorTab } from './ShareToDoctorTab';

const ContentWrapper = styled.div`
  width: 80%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 50px;

  ${p =>
    p.theme.isMobile &&
    `
    width: 100%;
    background: white;
    box-sizing: border-box;
    padding: 24px 28px;
    `}
`;

const Buttons = styled.div`
  margin-left: auto;
  display: flex;
  gap: 14px;
  align-items: center;
  flex-wrap: wrap;
  justify-content: flex-end;
`;

const BorderBottom = styled.div`
  width: 100%;
  padding: 11px 10px;
  border-bottom: 1px solid #e7e7e7;
`;

const DEFAULT_COUNT_PER_PAGE_OPTIONS = [5, 10, 25];

export const SendImages = () => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'features.sendImages',
  });
  const { t: globalT } = useTranslation('translation');
  const [user] = useUserState();

  const currentTime = moment().format(`${globalT('currentTimeWithoutLabel')}`);

  const [filter, setFilter] = useState<CaseFilter | undefined>();
  const [page, setPage] = useState(0);
  const [countPerPage, setCountPerPage] = useState(
    DEFAULT_COUNT_PER_PAGE_OPTIONS[0],
  );
  const {
    data: caseResponse,
    isFetching: isFetchingCases,
    refetch: refetchCase,
  } = useCase({ filter, page, size: countPerPage });
  const cases = caseResponse?.content ?? [];
  const totalCount = caseResponse?.totalElements;

  const { selected, selectOne, selectAll } = useServerSideSelected(
    cases,
    'caseId',
  );
  const [selectedCases, setSelectedCases] = useState<Case[]>([]);

  useEffect(() => {
    setSelectedCases(prev => {
      const notIncludedAndExists = selected
        .filter(caseId => !prev.find(c => c.caseId === caseId))
        .map(caseId => cases.find(c => c.caseId === caseId))
        .filter(c => !!c) as Case[];
      const present = prev.filter(c => selected.includes(c.caseId));
      return [...present, ...notIncludedAndExists];
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  const handleSearchFilter = (searchFilter?: CaseFilter) => {
    setPage(0);
    setFilter(searchFilter);
    selectAll(false);
  };

  return (
    <>
      <SectionContentContainer>
        <SectionHeading>{t('imageList.heading')}</SectionHeading>
        <ImageListTop
          left={<FilterSearchBar onSearch={handleSearchFilter} />}
          right={
            <Buttons>
              <CurrentTimeText>{currentTime}</CurrentTimeText>
              <ButtonWithIcon
                $padding="5px"
                icon={refreshIcon}
                text={`${t('imageList.refreshButton')}`}
                onClick={refetchCase}
              />
            </Buttons>
          }
        />
        <ImageListTable
          dataToShow={cases}
          data={cases}
          selected={selected}
          totalCount={totalCount}
          countPerPage={countPerPage}
          countPerPageOptions={DEFAULT_COUNT_PER_PAGE_OPTIONS}
          page={page}
          onChangePage={setPage}
          onChangeCountPerPage={newCountPerPage => {
            setPage(0);
            setCountPerPage(newCountPerPage);
          }}
          onSelect={selectOne}
          onSelectAll={selectAll}
          isLoading={isFetchingCases}
        />
      </SectionContentContainer>
      {user?.identified ? (
        <SelectedSection selectedCases={selectedCases} />
      ) : (
        <NotVerifiedSelectedSection selectedCases={selectedCases} />
      )}
    </>
  );
};

interface SelectedSectionProps {
  selectedCases: Case[];
}
const SelectedSection = ({ selectedCases }: SelectedSectionProps) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'features.sendImages',
  });

  const [tab, setTab] = useState(0);

  return (
    <SectionContentContainer>
      <Tabs $marginTop="28px">
        <>
          <Tab $width="277px" onClick={() => setTab(0)} $selected={tab === 0}>
            {t('tabs.0')}
          </Tab>
          <Tab $width="277px" onClick={() => setTab(1)} $selected={tab === 1}>
            {t('tabs.1')}
          </Tab>
        </>
      </Tabs>
      <ContentWrapper>
        {tab === 0 ? (
          <SendToInstitutionTab cases={selectedCases} />
        ) : (
          <ShareToDoctorTab cases={selectedCases} />
        )}
      </ContentWrapper>
    </SectionContentContainer>
  );
};

const NotVerifiedSelectedSection = ({
  selectedCases,
}: SelectedSectionProps) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'features.sendImages',
  });

  const [tab, setTab] = useState(1);

  return (
    <SectionContentContainer>
      <Tabs $marginTop="28px">
        <Tab $width="277px" onClick={() => setTab(1)} $selected={tab === 1}>
          {t('tabs.1')}
        </Tab>
        <BorderBottom />
      </Tabs>
      <ContentWrapper>
        <ShareToDoctorTab cases={selectedCases} />
      </ContentWrapper>
    </SectionContentContainer>
  );
};
